import React, { type ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { QuickSearchInput } from '~/features/catalog/components/quicksearch/QuickSearchInput';
import { useArticleSearchQueryParams } from '~/features/catalog/components/filter/useArticleSearchQueryParam';
import type { Area } from '~/common/types/area';
import { TgArea } from '~/common/types/area';
import { useTranslation } from 'react-i18next';
import { useQuickSearch } from '~/features/catalog/routes/resources/QuickSearchRoute';
import { LucideSearchX } from 'lucide-react';
import { QuickSearchSuggestionItem } from '~/features/catalog/components/quicksearch/QuickSearchSuggestionItem';
import type { AghArticle } from '~/common/types/aghArticle';
import type { BghArticle } from '~/common/types/bghArticle';
import { QuickSearchBghArticleItem } from '~/features/catalog/components/quicksearch/QuickSearchBghArticleItem';
import { QuickSearchAghArticleItem } from '~/features/catalog/components/quicksearch/QuickSearchAghArticleItem';
import { ScrollArea } from '~/common/components/ui/scroll-area';
import { useOnClickOutside } from 'usehooks-ts';
import { QUERY_SEARCH_PARAM_SEARCHTERM } from '~/features/catalog/mappers/catalogSearchQueryMapper';
import { useBasePath } from '~/common/hooks/useBasePath';
import { useNavigate, useSubmit } from 'react-router';
import { cn } from '~/common/utils/styleUtil';
import { ClientOnly } from 'remix-utils/client-only';
import { useScreenSize } from '~/common/hooks/useScreenSize';
import { useHasTopBanner } from '~/shell/hooks/useHasTopBanner';
import { isArticleNumber } from '~/features/catalog/utils/articleUtil.ts';

export function DesktopQuickSearch({
  isAuthenticated,
  area,
  onOpenChange
}: Readonly<{
  isAuthenticated: boolean;
  area: Area;
  onOpenChange: (open: boolean) => void;
}>) {
  const [isOpen, setIsOpen] = useState(false);
  const { urlSearchRequest } = useArticleSearchQueryParams();
  const [liveSearchTerm, setLiveSearchTerm] = useState(urlSearchRequest.searchTerm ?? '');
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const { result, search, isSearching } = useQuickSearch();
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const ref = useRef<HTMLInputElement>(null);
  const quicksearchRef = useRef<HTMLInputElement>(null);
  const { path } = useBasePath();
  const navigate = useNavigate();
  const { hasTopBanner } = useHasTopBanner();
  const { isQsDown } = useScreenSize();
  const submit = useSubmit();
  const [currentInputWidth, setCurrentInputWidth] = useState(0);

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    search(event.target.value);
  };

  const handleClear = useCallback(() => {
    setSearchTerm('');
    search('');

    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [search]);

  const setSearchInputRef = useCallback(inputElement => {
    searchInputRef.current = inputElement;
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const handleOnFocus = () => {
    setIsOpen(true);

    if (searchInputRef.current) {
      const input = searchInputRef.current;
      const length = input.value.length;
      setTimeout(() => {
        input.setSelectionRange(length, length);
      }, 0);
    }
  };

  const handleClickOutside = () => {
    setIsOpen(false);
  };

  const handleOnClose = () => {
    setIsOpen(false);
  };

  const handleArticleClick = (artNo: string) => {
    setIsOpen(false);
    navigate(`${path}/catalog/article/${artNo}`);
  };

  const handleSearchButtonClick = useCallback(() => {
    if (searchInputRef.current) {
      if (isOpen) {
        searchInputRef.current.blur();
        setIsOpen(false);

        if (searchTerm.trim().length >= 3) {
          setIsOpen(false);
          setLiveSearchTerm(searchTerm);

          const formData = new FormData();
          formData.set(QUERY_SEARCH_PARAM_SEARCHTERM, searchTerm.trim());
          submit(formData, {
            action: `${path}/catalog`,
            method: 'GET'
          });
        }
      } else {
        searchInputRef.current.focus();
      }
    }
  }, [isOpen, searchTerm, submit, path]);

  const handleSuggestionClick = (suggestion: string) => {
    setIsOpen(false);
    setLiveSearchTerm(suggestion); // Optimistic update
    const searchParams = new URLSearchParams();
    searchParams.set(QUERY_SEARCH_PARAM_SEARCHTERM, suggestion);
    navigate({
      pathname: `${path}/catalog`,
      search: `?${searchParams.toString()}`
    });
  };

  useOnClickOutside(ref as React.RefObject<HTMLElement>, handleClickOutside);

  // Blur search input on touch outside to close virtual keyboard on touch devices
  useOnClickOutside(
    quicksearchRef as React.RefObject<HTMLElement>,
    () => {
      if (isOpen) {
        if (searchInputRef.current) {
          searchInputRef.current.blur();
        }
      }
    },
    'touchstart'
  );

  useEffect(() => {
    onOpenChange(isOpen);
    if (searchInputRef.current) {
      searchInputRef.current.blur();
    }
  }, [isOpen, onOpenChange]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLiveSearchTerm(urlSearchRequest.searchTerm ?? '');
  }, [urlSearchRequest.searchTerm]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSearchTerm(liveSearchTerm);
    search(liveSearchTerm);
  }, [liveSearchTerm, search]);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!quicksearchRef.current || !isOpen) return;

    const updateWidth = () => {
      if (quicksearchRef.current) {
        const width = quicksearchRef.current.offsetWidth;
        if (isQsDown) {
          const calcWidth = width + (ref.current?.getBoundingClientRect().left || 0);
          setCurrentInputWidth(calcWidth);
        } else {
          setCurrentInputWidth(width);
        }
      }
    };

    requestAnimationFrame(() => {
      updateWidth();
    });

    // Window resize listener
    window.addEventListener('resize', updateWidth);

    return () => {
      window.removeEventListener('resize', updateWidth);
    };
  }, [isOpen, isQsDown]);

  const isValidSearchTerm = searchTerm.length >= 3;

  const scrollAreaWidth = `${currentInputWidth}px`;

  return (
    <>
      <ClientOnly>
        {() =>
          isOpen && (
            <div
              className={cn(
                'fixed h-full w-full left-0 right-0 z-40 bg-black/60',
                hasTopBanner ? 'top-[156px]' : 'top-[116px]'
              )}
            />
          )
        }
      </ClientOnly>

      <div ref={ref} className={'w-full'}>
        <h2 className="sr-only">{t('Suche')}</h2>
        <div ref={quicksearchRef}>
          <QuickSearchInput
            ref={setSearchInputRef}
            searchTerm={searchTerm}
            onChange={handleOnChange}
            onClear={handleClear}
            onFocus={handleOnFocus}
            onSubmit={() => handleOnClose()}
            onKeyDown={handleKeyDown}
            onSearchButtonClick={handleSearchButtonClick}
          />
        </div>
        <ClientOnly>
          {() => (
            <dialog
              open
              aria-label={t('Suche')}
              className={cn(
                `fixed z-40 left-0 w-screen max-w-none max-h-none bg-white text-inherit shadow-md overflow-hidden transition-all ease-in-out duration-300`,
                isQsDown && `duration-0`,
                isOpen ? 'h-[600px]' : 'h-0',
                hasTopBanner ? 'top-[156px]' : 'top-[116px]'
              )}
            >
              <ScrollArea
                style={{
                  width: scrollAreaWidth,
                  left: !isQsDown ? ref.current?.getBoundingClientRect().left : 0,
                  paddingLeft: isQsDown ? '32px' : 0
                }}
              >
                {isValidSearchTerm ? (
                  <>
                    {!isSearching &&
                      result.suggestions &&
                      result.suggestions?.length < 1 &&
                      result.articles &&
                      result.articles?.length < 1 && (
                        <div className={'flex flex-col items-center gap-4 text-secondary mt-8'}>
                          <LucideSearchX size={48} className={'text-secondary'} />
                          {t('Keine Artikel für diese Suche!')}
                        </div>
                      )}
                    <div className={'flex flex-col gap-8 desktop:flex-row p-4 desktop:px-0'}>
                      {result?.suggestions && result.suggestions.length > 0 && (
                        <div className={cn('2xl:mr-32 mr-8 xl:mr-16')}>
                          <p className={'font-bold text-lg mb-4'}>{t('Suchvorschläge')}</p>
                          <ul>
                            {result.suggestions?.map(suggestion => (
                              <QuickSearchSuggestionItem
                                key={suggestion}
                                searchTerm={searchTerm}
                                suggestion={suggestion}
                                onClick={handleSuggestionClick}
                              />
                            ))}
                          </ul>
                        </div>
                      )}
                      {result?.articles && result.articles.length > 0 && (
                        <div className={'grow'}>
                          <p className={'font-bold mb-4 text-lg'}>{t('Produktempfehlungen')}</p>
                          <ul>
                            {result.articles?.map((article: AghArticle | BghArticle) =>
                              area === TgArea ? (
                                <QuickSearchBghArticleItem
                                  key={article.articleNumber}
                                  article={article as BghArticle}
                                  onClick={handleArticleClick}
                                  isAuthenticated={isAuthenticated}
                                  selectedArticleNumber={isArticleNumber(searchTerm) ? searchTerm : undefined}
                                />
                              ) : (
                                <QuickSearchAghArticleItem
                                  key={article.articleNumber}
                                  article={article}
                                  onClick={handleArticleClick}
                                  selectedArticleNumber={isArticleNumber(searchTerm) ? searchTerm : undefined}
                                  isAuthenticated={isAuthenticated}
                                />
                              )
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className={'flex flex-col items-center gap-4 text-secondary mt-8'}>
                    {t('Suchvorschläge ab 3 Zeichen!')}
                  </div>
                )}
              </ScrollArea>
            </dialog>
          )}
        </ClientOnly>
      </div>
    </>
  );
}
